import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getEnabledChannels } from '@/lib/queries/channels'
import { getAllStockBundles } from '@/lib/queries/bundles'
import { productService } from '@/services/productService'
import { isSkuUnique } from '@/lib/queries/products'
import { uploadHeroImage } from '@/lib/media'

type ProductType = 'physical' | 'print_on_demand'

export default function CreateProduct() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1) // 1..9 (we'll implement core steps)
  const [channelId, setChannelId] = useState<string | null>(null)
  const [productType, setProductType] = useState<ProductType | null>(null)
  const [bundleSku, setBundleSku] = useState<string | null>(null)
  const [sku, setSku] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [basePrice, setBasePrice] = useState('')
  const [cost, setCost] = useState('')
  const [heroAssetId, setHeroAssetId] = useState<string | null>(null)
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(null)
  const [heroError, setHeroError] = useState<string | null>(null)
  const [isUploadingHero, setIsUploadingHero] = useState(false)
  const [podAssetId, setPodAssetId] = useState<string | null>(null)
  const [podPreviewUrl, setPodPreviewUrl] = useState<string | null>(null)
  const [podError, setPodError] = useState<string | null>(null)
  const [isUploadingPod, setIsUploadingPod] = useState(false)
  const [skuError, setSkuError] = useState<string | null>(null)
  const [isCheckingSku, setIsCheckingSku] = useState(false)

  const { data: channels = [] } = useQuery({ queryKey: ['channels', 'enabled'], queryFn: getEnabledChannels })
  const {
    data: bundles = [],
    isLoading: isLoadingBundles,
  } = useQuery({ queryKey: ['stockBundles', 'all'], queryFn: getAllStockBundles })

  const channelCode = useMemo(() => {
    const ch: any = (channels as any[]).find((c) => c._id === channelId)
    if (!ch) return null
    return ch.code || ch.integrationIdentifier || (ch.name || '').toLowerCase()
  }, [channels, channelId])

  const { mutateAsync: doCreate, isPending } = useMutation({
    mutationFn: productService.createProduct,
  })
  const [error, setError] = useState<string | null>(null)

  const getNextStep = (currentStep: number, type: ProductType | null): number => {
    if (currentStep === 1) return 2
    if (currentStep === 2) {
      if (type === 'print_on_demand') return 5
      if (type === 'physical') return 3
      return 2
    }
    if (currentStep === 3) return 5
    if (currentStep === 5) return 7
    if (currentStep === 7) return 9
    return Math.min(9, currentStep + 1)
  }

  const getPreviousStep = (currentStep: number, type: ProductType | null): number => {
    if (currentStep <= 1) return 1
    if (currentStep === 2) return 1
    if (currentStep === 3) return 2
    if (currentStep === 5) {
      if (type === 'print_on_demand') return 2
      if (type === 'physical') return 3
      return 2
    }
    if (currentStep === 7) return 5
    if (currentStep === 9) return 7
    return Math.max(1, currentStep - 1)
  }

  const canNext = useMemo(() => {
    if (step === 1) return !!channelId
    if (step === 2) return !!productType
    if (step === 3) {
      if (productType === 'physical') return !!bundleSku || (bundles as any[]).length === 0 // allow showing prerequisite
      return true
    }
    if (step === 5) return sku.trim().length > 0 && name.trim().length > 0 && !skuError && !isCheckingSku
    if (step === 7) {
      const numericPrice = parseFloat(basePrice)
      if (Number.isNaN(numericPrice) || numericPrice <= 0) return false
      if (productType === 'print_on_demand') {
        const numericCost = parseFloat(cost)
        return !Number.isNaN(numericCost) && numericCost > 0
      }
      // Physical products only require RRP
      return true
    }
    return true
  }, [step, channelId, productType, bundleSku, bundles, sku, name, skuError, isCheckingSku, basePrice, cost])

  const handleHeroChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setHeroError(null)
    if (!file.type.startsWith('image/')) {
      setHeroError('Please select an image file.')
      return
    }
    try {
      setIsUploadingHero(true)
      const { assetId, url } = await uploadHeroImage(file)
      setHeroAssetId(assetId)
      setHeroPreviewUrl(url ?? null)
    } catch (err: any) {
      setHeroError(err?.message || 'Failed to upload image.')
    } finally {
      setIsUploadingHero(false)
    }
  }

  const handlePodChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPodError(null)
    if (!file.type.startsWith('image/')) {
      setPodError('Please select an image file.')
      return
    }
    try {
      setIsUploadingPod(true)
      const { assetId, url } = await uploadHeroImage(file)
      setPodAssetId(assetId)
      setPodPreviewUrl(url ?? null)
    } catch (err: any) {
      setPodError(err?.message || 'Failed to upload image.')
    } finally {
      setIsUploadingPod(false)
    }
  }

  const goNext = async () => {
    if (step < 9) {
      // Validate SKU uniqueness when leaving details step
      if (step === 5) {
        const trimmedSku = sku.trim()
        if (!trimmedSku) return
        setIsCheckingSku(true)
        setSkuError(null)
        try {
          const unique = await isSkuUnique(trimmedSku)
          if (!unique) {
            setSkuError('SKU must be unique. Please choose a different SKU.')
            setIsCheckingSku(false)
            return
          }
        } catch {
          setSkuError('Could not validate SKU uniqueness. Please try again.')
          setIsCheckingSku(false)
          return
        }
        setIsCheckingSku(false)
      }

      setStep(getNextStep(step, productType))
      return
    }
    // Create
    setError(null)
    const numericBasePrice = basePrice ? parseFloat(basePrice) : 0
    const numericCost = cost ? parseFloat(cost) : undefined
    const media: any = {}
    if (heroAssetId != null) {
      media.hero = {
        _type: 'image',
        asset: {
          _type: 'reference',
          _ref: heroAssetId,
        },
      }
    }
    if (podAssetId != null) {
      media.pod = {
        _type: 'image',
        asset: {
          _type: 'reference',
          _ref: podAssetId,
        },
      }
    }
    const payload: any = {
      channelCode: channelCode || 'unknown',
      productType: productType || 'print_on_demand',
      sku: sku.trim(),
      name: name.trim(),
      description: description.trim(),
      images: [],
      basePrice: numericBasePrice,
      // Keep Sanity rrp in sync with our normalized basePrice
      rrp: numericBasePrice,
      ...(productType === 'print_on_demand' && numericCost !== undefined ? { cost: numericCost } : {}),
      ...(Object.keys(media).length > 0 ? { media } : {}),
    }
    if (productType === 'physical') payload.bundleSku = bundleSku || undefined
    try {
      const created = await doCreate(payload)
      if (created && (created as any)._id) {
        navigate(`/products/${(created as any)._id}`)
      } else {
        navigate('/products')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to create product')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900">Create Product</h1>
          <div className="mt-3 h-1 bg-gray-200 rounded">
            <div className="h-1 bg-blue-600 rounded" style={{ width: `${(step / 9) * 100}%` }} />
          </div>
          {/* Numbered step indicator */}
          <div className="mt-3 flex justify-between px-1">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
              const isActive = n === step
              const isCompleted = n < step
              return (
                <div
                  key={n}
                  aria-label={`Step ${n}`}
                  aria-current={isActive ? 'step' : undefined}
                  data-state={isActive ? 'active' : isCompleted ? 'completed' : 'inactive'}
                  className={[
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                    isActive ? 'bg-blue-600 text-white' : isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700',
                  ].join(' ')}
                >
                  {n}
                </div>
              )
            })}
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-400 text-red-900 rounded-lg px-4 py-3">
            {error}
          </div>
        )}
        {step === 1 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 1: Select Channel</h2>
            <div className="space-y-3">
              {(channels as any[]).map((ch) => {
                const selected = ch._id === channelId
                return (
                  <button
                    key={ch._id}
                    onClick={() => setChannelId(ch._id)}
                    className={`w-full text-left border rounded-lg p-3 ${
                      selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                    aria-pressed={selected}
                  >
                    <div className="font-medium">{ch.name}</div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {step === 2 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 2: Select Product Type</h2>
            <div className="space-y-3">
              <button
                className={`w-full text-left border rounded-lg p-3 ${
                  productType === 'physical' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                }`}
                onClick={() => setProductType('physical')}
              >
                <div className="font-medium">Physical Product</div>
                <div className="text-xs text-gray-600 mt-1">Requires a bundle</div>
              </button>
              <button
                className={`w-full text-left border rounded-lg p-3 ${
                  productType === 'print_on_demand' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
                }`}
                onClick={() => setProductType('print_on_demand')}
              >
                <div className="font-medium">Print on Demand</div>
                <div className="text-xs text-gray-600 mt-1">Uses Printify, no bundle needed</div>
              </button>
            </div>
          </section>
        )}

        {step === 3 && productType === 'physical' && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 3: Bundle Selection</h2>
            {isLoadingBundles ? (
              <div className="text-sm text-gray-500">Loading bundles...</div>
            ) : (bundles as any[]).length === 0 ? (
              <div className="prerequisite-alert bg-amber-50 border border-amber-400 rounded-lg p-3">
                <h4 className="text-amber-800 font-semibold mb-1">No Bundles Found</h4>
                <p className="text-sm text-amber-800">You need to create a bundle first before creating a physical product.</p>
                <a href="/bundles" className="inline-block mt-2 bg-blue-600 text-white px-3 py-2 rounded">
                  Create Bundle
                </a>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Select Bundle</label>
                <select
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
                  value={bundleSku || ''}
                  onChange={(e) => setBundleSku(e.target.value || null)}
                >
                  <option value="">Select a bundle</option>
                  {(bundles as any[]).map((b) => (
                    <option key={b._id} value={b.sku}>
                      {b.sku} - {b.title || b.name || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </section>
        )}

        {step === 5 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 5: Product Details</h2>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <input
                aria-label="SKU"
                type="text"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                value={sku}
                onChange={(e) => {
                  setSku(e.target.value)
                  if (skuError) {
                    setSkuError(null)
                  }
                }}
                placeholder="PROD-001"
              />
              {skuError && <p className="mt-1 text-sm text-red-600">{skuError}</p>}
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
              <input
                aria-label="Product Name"
                type="text"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter product name"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                aria-label="Description"
                rows={4}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter product description"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image</label>
              {heroPreviewUrl && (
                <div className="mb-2 relative inline-block">
                  <img
                    src={heroPreviewUrl}
                    alt="Hero preview"
                    className="w-full max-h-64 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    aria-label="Remove hero image"
                    className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-white/90 text-red-600 text-xs font-bold flex items-center justify-center shadow-sm border border-red-200"
                    onClick={() => {
                      setHeroAssetId(null)
                      setHeroPreviewUrl(null)
                      setHeroError(null)
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleHeroChange}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {heroError && <p className="mt-1 text-sm text-red-600">{heroError}</p>}
              {isUploadingHero && <p className="mt-1 text-xs text-gray-500">Uploading image…</p>}
            </div>
            {productType === 'print_on_demand' && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">POD Hero Image</label>
                {podPreviewUrl && (
                  <div className="mb-2 relative inline-block">
                    <img
                      src={podPreviewUrl}
                      alt="POD hero preview"
                      className="w-full max-h-64 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      aria-label="Remove POD hero image"
                      className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-white/90 text-red-600 text-xs font-bold flex items-center justify-center shadow-sm border border-red-200"
                      onClick={() => {
                        setPodAssetId(null)
                        setPodPreviewUrl(null)
                        setPodError(null)
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePodChange}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {podError && <p className="mt-1 text-sm text-red-600">{podError}</p>}
                {isUploadingPod && <p className="mt-1 text-xs text-gray-500">Uploading POD image…</p>}
              </div>
            )}
          </section>
        )}

        {step === 7 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 7: Set Pricing</h2>
            {productType === 'print_on_demand' ? (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cost">
                    Cost
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">£</span>
                    <input
                      id="cost"
                      aria-label="Cost"
                      type="number"
                      step="0.01"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="base-price">
                    Base Price
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">£</span>
                    <input
                      id="base-price"
                      aria-label="Base Price"
                      type="number"
                      step="0.01"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                      value={basePrice}
                      onChange={(e) => setBasePrice(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="base-price">
                  RRP
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-lg">$</span>
                  <input
                    id="base-price"
                    aria-label="RRP"
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                    value={basePrice}
                    onChange={(e) => setBasePrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </section>
        )}

        {step === 9 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">Step 9: Preview</h2>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="text-lg font-semibold mb-1">{name || 'Product Name'}</div>
              <div className="text-xs text-gray-600 mb-1">SKU: {sku || '—'}</div>
              <div className="text-xs text-gray-600 mb-1">Channel: {channelCode || '—'}</div>
              <div className="text-xs text-gray-600 mb-1">Type: {productType === 'physical' ? 'Physical' : 'POD'}</div>
              <div className="text-2xl font-bold text-blue-600 mt-2">£{(basePrice && parseFloat(basePrice).toFixed(2)) || '0.00'}</div>
            </div>
          </section>
        )}
      </main>

      <footer className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
        <button
          className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
          onClick={() => setStep((s) => getPreviousStep(s, productType))}
        >
          Back
        </button>
        {step < 9 ? (
          <button
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
            onClick={() => {
              if (canNext) {
                void goNext()
              }
            }}
            disabled={!canNext}
          >
            Next
          </button>
        ) : (
          <button
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
            onClick={goNext}
            disabled={isPending}
          >
            Create Product
          </button>
        )}
      </footer>
    </div>
  )
}

